/**
 * Favorites Store - Pinia store for wishlist, recently viewed, and product alerts
 * Manages user's favorite products, viewing history, and price/stock alerts
 */

import { defineStore } from 'pinia'
import api from '@/services/api'
import type {
  FavoritesState,
  FavoriteProduct,
  ProductAlert,
  CreateAlertPayload,
  ListFavoritesParams
} from './types/favorites.types'

export const useFavoritesStore = defineStore('favorites', {
  state: (): FavoritesState => ({
    favorites: [],
    filters: {
      q: '',
      page: 1,
      pageSize: 24,
      sort: 'added_desc'
    },
    pagination: {
      page: 1,
      pageSize: 24,
      total: 0,
      pageCount: 0
    },
    alerts: [],
    loading: {
      list: false,
      add: false,
      remove: false,
      alertsList: false,
      alertsCreate: false,
      alertsRemove: false
    },
    error: {
      list: '',
      add: '',
      remove: '',
      alertsList: '',
      alertsCreate: '',
      alertsRemove: ''
    }
  }),

  getters: {
    /**
     * Get all favorites
     */
    allFavorites(state): FavoriteProduct[] {
      return state.favorites
    },

    /**
     * Get favorites pagination
     */
    favoritesPagination(state) {
      return state.pagination
    },

    /**
     * Get favorites filters
     */
    favoritesFilters(state) {
      return state.filters
    },

    /**
     * Get loading states
     */
    favoritesLoading(state) {
      return state.loading
    },

    /**
     * Get error states
     */
    favoritesError(state) {
      return state.error
    },

    /**
     * Check if a product is in favorites
     */
    isFavorite(state) {
      return (productId: string): boolean => {
        return state.favorites.some(f =>
          `${f.productPlu}` === `${productId}` || `${f.id}` === `${productId}`
        )
      }
    },

    /**
     * Get product alerts
     */
    allAlerts(state): ProductAlert[] {
      return state.alerts
    }
  },

  actions: {
    /**
     * List favorites with pagination
     *
     * @param params - Optional filter parameters
     * @returns Promise with list of favorites
     */
    async listFavorites(params: ListFavoritesParams = {}): Promise<FavoriteProduct[]> {
      this.loading.list = true
      this.error.list = ''

      try {
        const merged = { ...this.filters, ...params }
        const response = await api.favorites.products.list({
          q: merged.q || undefined,
          page: merged.page,
          pageSize: merged.pageSize,
          sort: merged.sort
        })

        const items: FavoriteProduct[] = response.items || response.data || []
        this.favorites = items
        this.filters = merged
        this.pagination = {
          page: response.page || merged.page || 1,
          pageSize: response.pageSize || merged.pageSize || 24,
          total: response.total || items.length || 0,
          pageCount: response.pageCount || Math.ceil((response.total || 0) / (response.pageSize || merged.pageSize || 24))
        }

        return items
      } catch (error: any) {
        this.error.list = error?.message || 'Failed to load favorites'
        return []
      } finally {
        this.loading.list = false
      }
    },

    /**
     * Add a product to favorites
     *
     * @param payload - Object with storeId and productPlu
     * @returns Promise with updated favorites list
     */
    async addFavorite(payload: { storeId: string; productPlu: string }): Promise<FavoriteProduct[]> {
      this.loading.add = true
      this.error.add = ''

      try {
        await api.favorites.products.add(payload)
        return await this.listFavorites()
      } catch (error: any) {
        this.error.add = error?.message || 'Failed to add to wishlist'
        throw error
      } finally {
        this.loading.add = false
      }
    },

    /**
     * Remove a product from favorites
     *
     * @param idOrProductPlu - The favorite ID or product PLU to remove
     * @returns Promise with updated favorites list
     */
    async removeFavorite(idOrProductPlu: string): Promise<FavoriteProduct[]> {
      this.loading.remove = true
      this.error.remove = ''

      try {
        // API commonly requires favoriteId; fallback to lookup by productPlu
        const found = this.favorites.find(f =>
          `${f.id}` === `${idOrProductPlu}` || `${f.productPlu}` === `${idOrProductPlu}`
        )
        const favoriteId = found?.id ? String(found.id) : idOrProductPlu

        await api.favorites.products.remove(favoriteId)
        return await this.listFavorites({ page: this.pagination.page })
      } catch (error: any) {
        this.error.remove = error?.message || 'Failed to remove favorite'
        throw error
      } finally {
        this.loading.remove = false
      }
    },

    /**
     * Remove a product from favorites by store ID and product PLU
     *
     * @param storeId - The store ID
     * @param productPlu - The product PLU
     * @returns Promise with updated favorites list
     */
    async removeFavoriteByPlu(storeId: string, productPlu: string): Promise<FavoriteProduct[]> {
      this.loading.remove = true
      this.error.remove = ''

      try {
        await api.favorites.products.removeByPlu(storeId, productPlu)
        return await this.listFavorites({ page: this.pagination.page })
      } catch (error: any) {
        this.error.remove = error?.message || 'Failed to remove favorite'
        throw error
      } finally {
        this.loading.remove = false
      }
    },

    /**
     * List product alerts
     *
     * @returns Promise with list of alerts
     */
    async listAlerts(): Promise<ProductAlert[]> {
      this.loading.alertsList = true
      this.error.alertsList = ''

      try {
        const response = await api.user.productAlerts.list()
        const items =
          (response as any)?.items || (response as any)?.data || (response as any) || []
        this.alerts = items as ProductAlert[]
        return this.alerts
      } catch (error: any) {
        this.error.alertsList = error?.message || 'Failed to load alerts'
        return []
      } finally {
        this.loading.alertsList = false
      }
    },

    /**
     * Create a product alert
     *
     * @param payload - Alert creation payload (storeId, productPlu, alertType, targetPrice)
     * @returns Promise with updated alerts list
     */
    async createAlert(payload: CreateAlertPayload): Promise<ProductAlert[]> {
      this.loading.alertsCreate = true
      this.error.alertsCreate = ''

      try {
        await api.user.productAlerts.create(payload)
        return await this.listAlerts()
      } catch (error: any) {
        this.error.alertsCreate = error?.message || 'Failed to create alert'
        throw error
      } finally {
        this.loading.alertsCreate = false
      }
    },

    /**
     * Remove a product alert
     *
     * @param id - The alert ID to remove
     * @returns Promise with updated alerts list
     */
    async removeAlert(id: string): Promise<ProductAlert[]> {
      this.loading.alertsRemove = true
      this.error.alertsRemove = ''

      try {
        await api.user.productAlerts.remove(id)
        return await this.listAlerts()
      } catch (error: any) {
        this.error.alertsRemove = error?.message || 'Failed to remove alert'
        throw error
      } finally {
        this.loading.alertsRemove = false
      }
    }
  }
})
